<template>
  <div
    class="bg-white mb-[24px]"
  >
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-[16px] md:gap-[8px] mb-[24px] px-[20px] py-[16px] border-b-[1px] border-b-[#E5E5E5]">
      <div class="flex items-center gap-[8px] shrink-0">
        <Button
          outline
          type="icon"
          @click="$router.go(-1)"
        >
          <span class="k-arrow-left-2 text-[#0A0A0A]" />
        </Button>
        <span class="text-[#0A0A0A] font-semibold">
          Detail Total Nilai Transaksi
        </span>
      </div>
      <div class="flex items-center justify-between md:justify-end gap-[8px] w-full md:w-auto">
        <Button
          type="ghost"
          class="px-0 shrink-0"
          :disabled="isLoadingRefresh"
          @click="handleRefreshData"
        >
          <img
            class="cursor-pointer"
            :class="{ 'animate-spin' : isLoadingRefresh }"
            src="https://storage.googleapis.com/komerce/assets/icons/refresh-2.svg"
            alt="refresh data"
            width="20"
            height="20"
          >
        </Button>
        <DateRangeFilter
          v-model="period"
          :max-date="today"
          class="w-full md:max-w-[300px]"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[16px] mx-[24px] mb-[24px]">
      <TransactionTypeCard
        :active="activeCard === 'tbf'"
        title="Transaksi Biaya Fulfillment"
        :to="buildQueryString({ card: 'tbf', tab: 'komship' }, $route.query)"
        :loading="loadingSummaries"
      >
        <template #footer>
          <TBFCardToggle
            :loading-summaries="loadingSummaries"
            :summaries-data="summariesData"
          />
        </template>
      </TransactionTypeCard>
      <TransactionTypeCard
        :active="activeCard === 'tbp'"
        title="Transaksi Biaya Penarikan"
        :to="buildQueryString({ card: 'tbp', tab: 'withdrawal' }, $route.query)"
        :loading="loadingSummaries"
        :transaction="summariesData.transaction_item_withdrawal"
        :icon="{
          src: 'https://storage.googleapis.com/komerce/icon/money-out.svg',
          containerClass: 'bg-[#FFF7ED]',
        }"
      />
      <TransactionTypeCard
        :active="activeCard === 'tblms'"
        title="Transaksi Biaya Low Moving Stock"
        :to="buildQueryString({ card: 'tblms', tab: '' }, $route.query)"
        :loading="loadingSummaries"
        :transaction="summariesData.transaction_low_moving_stock"
        :icon="{
          src: 'https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/ticket-star.svg',
          containerClass: 'bg-[#FEF2F2]',
        }"
      />
    </div>

    <div class="mx-[24px]">
      <div
        v-if="activeCard === 'tbf'"
      >
        <FulfillmentCard
          ref="tbf"
          :period="period"
        />
      </div>
      <div
        v-if="activeCard === 'tbp'"
      >
        <PenarikanCard
          ref="tbp"
          :period="period"
          :summary="{ revenue_withdrawal: summariesData.revenue_item_withdrawal || 0, is_loading: loadingSummaries}"
        />
      </div>
      <div
        v-if="activeCard === 'tblms'"
      >
        <LowMovingStockCard
          ref="tblms"
          :period="period"
          :summary="{ revenue: summariesData.revenue_low_moving_stock || 0, is_loading: loadingSummaries}"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { today, last30 } from '@/store/helpers'
import { DAY_MONTH_YEAR, YEAR_MONTH_DAY } from '@/libs/filterDate'
import { IDR } from '@/libs/currency'
import { Button } from '@core/components/ui'
import DateRangeFilter from './components/DateRangeFilter.vue'
import FulfillmentCard from './components/FulfillmentCard.vue'
import PenarikanCard from './components/PenarikanCard.vue'
import LowMovingStockCard from './components/LowMovingStockCard.vue'
import buildQueryString from './helpers/buildQueryString'
import TransactionTypeCard from './components/TransactionTypeCard.vue'
import TBFCardToggle from './components/TBFCardToggle.vue'

/**
 * Component untuk menampilkan halaman total nilai transaksi kompack
 * @component TotalNilaiTransaksi
 */
export default {
  components: {
    FulfillmentCard,
    PenarikanCard,
    LowMovingStockCard,
    TransactionTypeCard,
    TBFCardToggle,
    Button,
    DateRangeFilter,
  },
  data() {
    return {
      today,
      /** @type {Object} Periode tanggal untuk filter */
      period: {
        startDate: this.$route.query.start_date || last30,
        endDate: this.$route.query.end_date || today,
      },
      summariesData: {},
      loadingSummaries: false,
      isLoadingRefresh: false,
    }
  },
  computed: {
    /**
     * Card aktif berdasarkan query parameter
     * @returns {string} Nama card aktif (tbf/tbp/tblms)
     * @note tbf: Transaksi Biaya Fulfillment
     * @note tbp: Transaksi Biaya Penarikan
     * @note tblms: Transaksi Biaya Low Moving Stock
     */
    activeCard() {
      return this.$route.query.card || 'tbf'
    },
  },
  watch: {
    period: {
      handler(period) {
        this.updatePeriod(period)
        this.fnFetchSummariesData()
      },
    },
  },
  created() {
    this.customBackLink = `/admin-dashboard-kompack?ss=${YEAR_MONTH_DAY(this.period.startDate)}&se=${YEAR_MONTH_DAY(this.period.endDate)}`
  },
  mounted() {
    this.fnFetchSummariesData()
  },
  methods: {
    DAY_MONTH_YEAR,
    IDR,
    buildQueryString,
    /**
     * Handle refresh data pada card yang aktif
     */
    async handleRefreshData() {
      this.isLoadingRefresh = true
      const fetchList = []
      try {
        if (this.activeCard === 'tbf') {
          fetchList.push(this.$refs.tbf.handleRefreshData())
        }
        if (this.activeCard === 'tbp') {
          fetchList.push(this.$refs.tbp.handleRefreshData())
        }
        if (this.activeCard === 'tblms') {
          fetchList.push(this.$refs.tblms.handleRefreshData())
        }
        fetchList.push(this.fnFetchSummariesData())
        await Promise.allSettled(fetchList)
      } finally {
        this.isLoadingRefresh = false
      }
    },
    /**
     * Update periode di URL query parameters
     * @param {Object} period - Objek periode dengan startDate dan endDate
     */
    updatePeriod(period) {
      this.$router.replace(buildQueryString({
        start_date: YEAR_MONTH_DAY(period.startDate),
        end_date: YEAR_MONTH_DAY(period.endDate),
      }, this.$route.query))
    },
    async fnFetchSummariesData() {
      this.loadingSummaries = true
      try {
        const { data } = await this.$http_new.get('/komship/api/v1/admin/transactions/kompack/fulfillment-detail-summaries', {
          params: {
            start_date: YEAR_MONTH_DAY(this.period.startDate),
            end_date: YEAR_MONTH_DAY(this.period.endDate),
          },
        })
        this.summariesData = data.data
      } catch (error) {
        this.$toast_error({
          message: 'Gagal mengambil data ringkasan, silahkan coba lagi',
        })
      }
      this.loadingSummaries = false
    },
  },
}
</script>
